"use client";
import { useState, useMemo } from 'react'
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { cn } from '@/lib/utils';

import { ShoppingCart, MessageCircle, Tag, Package, ChevronDown } from 'lucide-react'
import { toast } from 'sonner'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card'
import { useQuoteStore } from '@/stores/quoteStore'
import type { Product } from '@/types'

interface ProductCardProps {
  product: Product
  inBundle?: boolean
  onQuickInquiry?: (product: Product) => void
}

const COLOUR_MAP: Record<string, string> = {
  Silver: '#C0C0C0',
  Chrome: '#BEC2CB',
  Gold: '#D4AF37',
  Black: '#2D2D2D',
  Galvanised: '#8A9A8A',
  Antique: '#8B7355',
  Nickel: '#A19D94',
  Mirror: '#E8E8E8',
  'Rose Gold': '#E0BFB8',
  Copper: '#B87333',
}

export function ProductCard({ product, onQuickInquiry }: ProductCardProps) {
  const router = useRouter()
  const addItem = useQuoteStore((s) => s.addItem)
  const items = useQuoteStore((s) => s.items)
  const primaryVariant = product.variants?.find((v) => v.is_primary) ?? product.variants?.[0]
  const [activeVariantIdx, setActiveVariantIdx] = useState(0)
  const activeVariant = product.variants?.[activeVariantIdx] ?? primaryVariant
  const inQuote = !!activeVariant && items.some((i) => i.variantId === activeVariant.id)

  const uniqueColours = useMemo(() => {
    if ((product as any).uniqueColours) return (product as any).uniqueColours as { colour: string; material: string }[]
    const seen = new Set<string>()
    const result: { colour: string; material: string }[] = []
    product.variants?.forEach((v) => {
      if (v.colour) {
        const key = `${v.colour}-${v.material}`
        if (!seen.has(key)) {
          seen.add(key)
          result.push({ colour: v.colour, material: v.material })
        }
      }
    })
    return result
  }, [product.variants, (product as any).uniqueColours])

  const uniqueSizes = useMemo(() => {
    if ((product as any).uniqueSizes) return (product as any).uniqueSizes as string[]
    const seen = new Set<string>()
    const result: string[] = []
    product.variants?.forEach((v) => {
      if (v.size && !seen.has(v.size)) {
        seen.add(v.size)
        result.push(v.size)
      }
    })
    return result
  }, [product.variants, (product as any).uniqueSizes])

  function handleAddToQuote(e: React.MouseEvent) {
    e.preventDefault()
    if (!activeVariant) return
    addItem({
      variantId: activeVariant.id,
      sku: activeVariant.sku,
      productName: product.name,
      size: activeVariant.size,
      material: activeVariant.material,
      finish: activeVariant.finish,
      imageUrl: activeVariant.image_url,
    })
    toast.success(`${product.name} added to quote`, { description: `SKU: ${activeVariant.sku}` })
  }

  return (
    <Card className="group overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-border/40 bg-card h-full flex flex-col p-3 gap-0 rounded-2xl">
      <Link href={`/product${activeVariant?.slug_url || `/${product.slug}`}`} className="block">
        <div className="relative aspect-[4/3] bg-[#f5f5f5] rounded-xl overflow-hidden">
          <Image
            src={product.image_url || `https://placehold.co/400x300/f5f0e8/2d2d2d?text=${encodeURIComponent(product.name)}`}
            alt={product.name}
            fill
            sizes="(max-width: 640px) 85vw, (max-width: 1280px) 50vw, 33vw"
            unoptimized={!product.image_url}
            className="object-contain group-hover:scale-[1.04] transition-transform duration-500 mix-blend-multiply"
            quality={75}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.srcset = '';
              target.src = `https://placehold.co/400x300/f5f0e8/2d2d2d?text=${encodeURIComponent(product.name)}`;
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

          {primaryVariant && (
            <div className="absolute bottom-3 left-3 px-3 py-1.5 bg-white/95 backdrop-blur rounded-full text-[11px] font-bold text-brand-accent shadow-sm tracking-wide">
              {primaryVariant.sku}
            </div>
          )}
        </div>
      </Link>

      <div className="p-3 pt-4 flex flex-col flex-1 gap-0">
        <Link href={`/product${activeVariant?.slug_url || `/${product.slug}`}`}>
          <h3 className="font-display font-bold text-lg leading-tight hover:text-brand-accent transition-colors line-clamp-2 text-foreground">
            {product.name} {activeVariant && `- ${activeVariant.size.replace('"', ' Inch')} (${activeVariant.colour})`}
          </h3>
        </Link>
        {activeVariant && (
          <p className="text-sm text-muted-foreground mt-1.5">
            {activeVariant.material} &bull; {activeVariant.finish}
          </p>
        )}

        <hr className="my-4 border-border/60" />

        {product.variants && product.variants.length > 0 && (
          <div className="flex items-center gap-4 text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-4">
            <div className="flex items-center gap-1.5">
              <Tag className="w-3.5 h-3.5 text-brand-accent" />
              <span>{uniqueColours.length} {uniqueColours.length === 1 ? 'COLOR' : 'COLORS'}</span>
            </div>
            <div className="w-[1px] h-3 bg-border" />
            {uniqueSizes.length > 0 ? (
              <HoverCard openDelay={100} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <div className="flex items-center gap-1 cursor-pointer hover:text-brand-accent transition-colors group">
                    <Package className="w-3.5 h-3.5 text-brand-accent mr-0.5" />
                    <span>{uniqueSizes.length} {uniqueSizes.length === 1 ? 'SIZE' : 'SIZES'}</span>
                    <ChevronDown className="w-3 h-3 text-muted-foreground group-hover:text-brand-accent transition-transform group-data-[state=open]:rotate-180" />
                  </div>
                </HoverCardTrigger>
                <HoverCardContent side="top" className="w-auto p-3" onClick={(e) => e.stopPropagation()}>
                  <p className="text-xs font-semibold mb-2">Available Sizes</p>
                  <div className="flex flex-wrap gap-1.5 max-w-[220px]">
                    {uniqueSizes.slice(0, 6).map((size) => {
                      const isActive = activeVariant?.size === size
                      return (
                        <button
                          key={size}
                          onClick={(e) => {
                            e.preventDefault()
                            // Try matching same colour first, otherwise fallback to first variant with this size
                            const varIdx = product.variants?.findIndex((v) => v.size === size && v.colour === activeVariant?.colour) ?? -1
                            if (varIdx !== -1) {
                              setActiveVariantIdx(varIdx)
                            } else {
                              const fallbackIdx = product.variants?.findIndex((v) => v.size === size) ?? 0
                              if (fallbackIdx !== -1) setActiveVariantIdx(fallbackIdx)
                            }
                          }}
                          className={cn(
                            "px-2 py-1 text-[11px] font-medium rounded border transition-colors",
                            isActive ? "border-brand-accent bg-brand-accent/10 text-brand-accent" : "border-border hover:border-brand-accent/50 hover:bg-muted"
                          )}
                        >
                          {size}
                        </button>
                      )
                    })}
                    {uniqueSizes.length > 6 && (
                      <Link href={`/product${activeVariant?.slug_url || `/${product.slug}`}`} className="px-2 py-1 text-[11px] font-medium text-brand-accent hover:underline flex items-center">
                        + View all {uniqueSizes.length} sizes
                      </Link>
                    )}
                  </div>
                </HoverCardContent>
              </HoverCard>
            ) : (
              <div className="flex items-center gap-1.5">
                <Package className="w-3.5 h-3.5 text-brand-accent" />
                <span>0 SIZES</span>
              </div>
            )}
          </div>
        )}


        {uniqueColours.length > 1 && (
          <div className="flex items-center gap-2 flex-wrap mb-5">
            {uniqueColours.slice(0, 5).map((cv) => {
              const isActive = activeVariant?.colour === cv.colour
              return (
                <button
                  key={`${cv.colour}-${cv.material}`}
                  className={cn(
                    "w-6 h-6 rounded-full transition-all hover:scale-110 shadow-sm border", 
                    isActive ? "border-brand-accent ring-2 ring-brand-accent/20" : "border-black/10"
                  )}
                  style={{
                    backgroundColor: COLOUR_MAP[cv.colour] ?? '#999',
                  }}
                  aria-label={`Select ${cv.colour} ${cv.material} variant`}
                  onClick={(e) => {
                    e.preventDefault()
                    // Try matching the same size first, otherwise fallback to first variant with this colour
                    const varIdx = product.variants?.findIndex((v) => v.colour === cv.colour && v.size === activeVariant?.size) ?? -1
                    if (varIdx !== -1) {
                      setActiveVariantIdx(varIdx)
                    } else {
                      const fallbackIdx = product.variants?.findIndex((v) => v.colour === cv.colour) ?? 0
                      if (fallbackIdx !== -1) setActiveVariantIdx(fallbackIdx)
                    }
                  }}
                  title={`${cv.colour} — ${cv.material}`}
                />
              )
            })}
            {uniqueColours.length > 5 && (
              <span className="text-xs text-muted-foreground font-medium pl-1">+{uniqueColours.length - 5}</span>
            )}
          </div>
        )}

        <div className="flex gap-2 mt-auto">
          <Button
            variant="outline"
            className="flex-1 h-10 rounded-md text-sm font-medium border-border/80 text-foreground hover:bg-muted/50"
            onClick={(e) => {
              e.preventDefault()
              if (onQuickInquiry) {
                onQuickInquiry(product)
              } else {
                router.push(`/quick-inquiry?sku=${activeVariant?.sku ?? ''}`)
              }
            }}
          >
            <MessageCircle className="h-4 w-4" />
            Inquire
          </Button>
          <Button
            className="flex-1 h-10 rounded-md text-sm font-medium bg-[#0f172a] hover:bg-[#0f172a]/90 text-white"
            onClick={handleAddToQuote}
            disabled={inQuote}
          >
            <ShoppingCart className="h-4 w-4" />
            {inQuote ? 'Added' : 'Add to Quote'}
          </Button>
        </div>
      </div>
    </Card>
  )
}
